import type { AuthenticatedUser, LoginRequest } from './types'
import { apiFetch } from '../../shared/http/apiFetch'

type CsrfResponse = { token: string }

export async function getCsrfToken(): Promise<string> {
  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('auth.csrfUnavailable')
  }
  const payload = (await response.json()) as CsrfResponse
  return payload.token
}

export async function getCurrentUser(
  signal?: AbortSignal,
): Promise<AuthenticatedUser | null> {
  const response = await fetch('/api/v1/auth/me', {
    credentials: 'include',
    signal,
  })
  if (response.status === 401) {
    return null
  }
  if (!response.ok) {
    throw new Error('auth.sessionUnavailable')
  }
  return response.json() as Promise<AuthenticatedUser>
}

export async function login(
  request: LoginRequest,
  csrfToken: string,
): Promise<AuthenticatedUser> {
  const response = await fetch('/api/v1/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as {
      code?: string
    } | null
    throw new Error(problem?.code ?? (
      response.status === 400
        ? 'auth.csrfUnavailable'
        : 'auth.invalidCredentials'
    ))
  }

  return response.json() as Promise<AuthenticatedUser>
}

export async function heartbeat(
  csrfToken: string,
): Promise<'active' | 'expired' | 'unavailable'> {
  const response = await fetch('/api/v1/auth/heartbeat', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-TOKEN': csrfToken },
  })

  if (response.ok) {
    return 'active'
  }

  return response.status === 401 ? 'expired' : 'unavailable'
}

export async function logout(csrfToken: string): Promise<void> {
  await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-TOKEN': csrfToken },
  })
}

export async function setActiveCompany(
  companyId: string,
  csrfToken: string,
): Promise<void> {
  const response = await apiFetch('/api/v1/me/active-company', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({ companyId }),
  })
  if (!response.ok) {
    throw new Error('administration.companyAccessDenied')
  }
}

export async function setCulture(
  culture: string,
  csrfToken: string,
): Promise<void> {
  const response = await apiFetch('/api/v1/auth/culture', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({ culture }),
  })
  if (!response.ok) {
    throw new Error('user.invalidLanguage')
  }
}

export async function setTheme(
  themeMode: 'System' | 'Light' | 'Dark',
  csrfToken: string,
): Promise<void> {
  const response = await apiFetch('/api/v1/auth/theme', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({ themeMode }),
  })
  if (!response.ok) throw new Error('user.invalidTheme')
}
